import { FC, useMemo } from "react"

import If from "../../../../if"
import Tooltip from "../../../../tool-tip"
import Badge from "../../../../badge"
import { PartialLoader } from "../../../../skeletons"
import {
  useFetchLoanOfficerQuery,
  useFetchSubscriptionEventsQuery
} from "../../../../../../redux/queries"

interface Props {
  id: string
  loid: string
  className?: string
  smsFollowupSent?: "0" | "1"
}

const EngagementsEvents: FC<Props> = ({
  id,
  loid,
  className,
  smsFollowupSent
}) => {
  const { data: events, isFetching } = useFetchSubscriptionEventsQuery(
    { subscription_id: id },
    { skip: !id }
  )
  const { data: loanOfficer, isFetching: isFetchingLoanOfficer } =
    useFetchLoanOfficerQuery(loid, {
      skip: !loid
    })

  // Check if SMS has been sent using the new field
  const isSMSSent = useMemo(
    () =>
      !isFetchingLoanOfficer &&
      !!loanOfficer?.sms_allowed &&
      smsFollowupSent === "1",
    [isFetchingLoanOfficer, loanOfficer?.sms_allowed, smsFollowupSent]
  )

  // Set the count of each type of SubscriptionEvents
  const dashboardPageviewCount = useMemo(() => {
    return (
      events?.filter(event => event.type === "dashboard_pageview").length ?? 0
    )
  }, [events])

  const rateAlertEmailOpenCount = useMemo(() => {
    return (
      events?.filter(event => event.type === "rate_alert_email_open").length ??
      0
    )
  }, [events])

  const eventCount = dashboardPageviewCount + rateAlertEmailOpenCount

  return (
    <If
      isLoading={isFetching}
      loadingComponent={<PartialLoader />}
      condition={eventCount > 0 || isSMSSent}
      then={
        <>
          <If
            condition={isSMSSent}
            then={
              <Badge.Alt className={`${className} ml-1`}>SMS sent</Badge.Alt>
            }
          />
          <If
            condition={eventCount > 0}
            then={
              <Tooltip
                displayTextClass="justify-self-start ml-1"
                toolTipNode={<span>🔥 {eventCount}+</span>}
                text={
                  <ul className="text-[10px]">
                    <If
                      condition={dashboardPageviewCount > 0}
                      then={
                        <li>
                          <span className="inline-block mr-1 -rotate-90">
                            🧲
                          </span>
                          &nbsp;Dashboard pageview {dashboardPageviewCount}x
                        </li>
                      }
                    />
                    <If
                      condition={rateAlertEmailOpenCount > 0}
                      then={
                        <li>
                          <span className="inline-block mr-1">📧</span>
                          &nbsp;Rate alert opened {rateAlertEmailOpenCount}x
                        </li>
                      }
                    />
                  </ul>
                }
                placement="top"
              />
            }
          />
        </>
      }
    />
  )
}

export default EngagementsEvents
